// Keep the full server text separate from the fitted text across LiveView patches.
//
// Every card on a list mounts this hook, and each probe of `scrollHeight`
// after a text change costs a full style recalc + layout of the document
// (~14 ms on the /aios lists, whatever the text length). Measuring card by
// card multiplied that by 42 cards × ~12 probes × 3 runs: three 5-second
// tasks before the Agents list appeared (28/10). The fit is therefore computed
// for every pending element at once, round by round: one write phase for all
// probes, then one read phase, so a binary-search round costs one layout for
// the whole list instead of one per card.
const pending = new Set()
let flushFrame = 0

export default {
  mounted() {
    this.scheduleClamp = () => schedule(this)
    this.clampObserver = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width
      if (width !== this.clampWidth) {
        this.clampWidth = width
        this.scheduleClamp()
      }
    })
    this.clampObserver.observe(this.el)
    this.scheduleClamp()
    if (document.fonts.status !== "loaded") {
      document.fonts.ready.then(() => {
        if (this.el.isConnected && !this.clampDestroyed) this.scheduleClamp()
      })
    }
  },
  updated() { this.scheduleClamp() },
  destroyed() {
    this.clampDestroyed = true
    this.clampObserver.disconnect()
    pending.delete(this)
  }
}

function schedule(hook) {
  pending.add(hook)
  if (!flushFrame) flushFrame = requestAnimationFrame(flush)
}

function flush() {
  flushFrame = 0
  const hooks = Array.from(pending)
  pending.clear()
  fitAll(hooks.filter(hook => !hook.clampDestroyed && hook.el.isConnected))
}

// Fits every hook's element in lock-step rounds (see the header comment).
export function fitAll(hooks) {
  // Read phase: widths and limits, one layout for all.
  const jobs = []
  for (const hook of hooks) {
    const width = hook.el.clientWidth
    if (!width) continue
    hook.clampWidth = hook.el.getBoundingClientRect().width
    jobs.push({
      hook,
      el: hook.el,
      text: hook.el.dataset.fullText || "",
      limit: parseFloat(getComputedStyle(hook.el).lineHeight) * 3 + 1,
      width
    })
  }
  if (jobs.length === 0) return

  // Write phase: one out-of-flow probe per element, with the full text.
  for (const job of jobs) {
    const probe = job.el.cloneNode(false)
    probe.removeAttribute("id")
    probe.removeAttribute("phx-hook")
    probe.setAttribute("aria-hidden", "true")
    probe.style.cssText =
      `position:absolute;left:-99999px;top:0;width:${job.width}px;box-sizing:border-box;` +
      "max-height:none;height:auto;visibility:hidden;pointer-events:none;" +
      "display:block;-webkit-line-clamp:unset;overflow:visible"
    probe.textContent = job.text
    job.el.parentNode.appendChild(probe)
    job.probe = probe
  }

  try {
    // Read phase: the elements whose full text fits are done.
    let open = []
    for (const job of jobs) {
      if (job.probe.scrollHeight <= job.limit) job.fitted = job.text
      else {
        job.characters = Array.from(job.text)
        job.low = 0
        job.high = job.characters.length
        open.push(job)
      }
    }

    // Binary search, one write phase and one read phase per round.
    while (open.length > 0) {
      for (const job of open) {
        job.middle = Math.ceil((job.low + job.high) / 2)
        job.probe.textContent = job.characters.slice(0, job.middle).join("").trimEnd() + " (...)"
      }
      const next = []
      for (const job of open) {
        if (job.probe.scrollHeight <= job.limit) job.low = job.middle
        else job.high = job.middle - 1
        if (job.low < job.high) next.push(job)
        else job.fitted = job.characters.slice(0, job.low).join("").trimEnd() + " (...)"
      }
      open = next
    }
  } finally {
    for (const job of jobs) job.probe.remove()
  }

  // Write phase: the fitted texts, one layout for the whole list afterwards.
  for (const job of jobs) {
    if (job.el.textContent !== job.fitted) job.el.textContent = job.fitted
  }
}
